<template>
    <div
        class="group-name-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-name-modal-title"
        @click.self="closePopup"
    >
        <div class="group-name-modal__dialog">
            <div class="group-name-modal__header">
                <h3
                    id="group-name-modal-title"
                    class="group-name-modal__title"
                >
                    Update Group Name
                </h3>

                <button
                    type="button"
                    class="group-name-modal__close"
                    aria-label="Close update group name popup"
                    :disabled="submitted"
                    @click="closePopup"
                >
                    <i
                        class="fa fa-times"
                        aria-hidden="true"
                    ></i>
                </button>
            </div>

            <div class="group-name-modal__body">
                <div
                    v-if="isLoading"
                    class="group-name-modal__loading"
                >
                    <i
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>Loading group information...</span>
                </div>

                <form
                    v-else
                    class="group-name-form"
                    @submit.prevent="submitGroupName"
                >
                    <div class="group-name-form__field">
                        <label
                            for="group-name"
                            class="group-name-form__label"
                        >
                            Group Name
                        </label>

                        <input
                            id="group-name"
                            v-model.trim="topic"
                            v-validate="'required|max:100'"
                            type="text"
                            name="topic"
                            class="group-name-form__input"
                            data-vv-as="group name"
                            maxlength="100"
                            autocomplete="off"
                            :disabled="submitted"
                            :class="{
                                'group-name-form__input--invalid':
                                    errors.has('topic')
                            }"
                        />

                        <small
                            v-if="errors.has('topic')"
                            class="group-name-form__error"
                        >
                            {{ errors.first('topic') }}
                        </small>
                    </div>

                    <div class="group-name-form__actions">
                        <button
                            type="button"
                            class="
                                group-name-form__button
                                group-name-form__button--cancel
                            "
                            :disabled="submitted"
                            @click="closePopup"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            class="
                                group-name-form__button
                                group-name-form__button--submit
                            "
                            :disabled="isSubmitDisabled"
                        >
                            <i
                                v-if="submitted"
                                class="fa fa-spinner fa-spin"
                                aria-hidden="true"
                            ></i>

                            <span>
                                {{ submitted ? 'Updating...' : 'Update' }}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { bus } from '@/main';
import toast from '@/services/toast';

const BODY_SCROLL_CLASS = 'body_right_scroll_remove';

export default {
    name: 'UpdateGroupName',

    props: {
        updateGroupNameID: {
            type: [String, Number],
            required: true
        }
    },

    data() {
        return {
            topic: '',
            initialTopic: '',
            submitted: false,
            isLoading: false
        };
    },

    computed: {
        normalizedTopic() {
            return String(this.topic || '').trim();
        },

        hasGroupNameChanged() {
            return this.normalizedTopic !== this.initialTopic;
        },

        isSubmitDisabled() {
            return (
                this.submitted ||
                this.isLoading ||
                !this.normalizedTopic ||
                !this.hasGroupNameChanged
            );
        }
    },

    mounted() {
        document.body.classList.add(BODY_SCROLL_CLASS);
        document.addEventListener('keydown', this.handleKeydown);

        this.fetchGroupInfo();
    },

    beforeDestroy() {
        this.removeGlobalListeners();
    },

    methods: {
        async fetchGroupInfo(emitUpdatedData = false) {
            if (!this.updateGroupNameID) {
                toast.error('Group information is unavailable.');
                return null;
            }

            if (!emitUpdatedData) {
                this.isLoading = true;
            }

            try {
                const response = await this.$store.dispatch(
                    'fetchGroupChatDetailsById',
                    {
                        id: this.updateGroupNameID
                    }
                );

                const groupData = this.getGroupData(response);

                if (!groupData) {
                    throw new Error('Group information was not found.');
                }

                const groupName =
                    groupData.group_name ||
                    groupData.name ||
                    '';

                this.topic = groupName;
                this.initialTopic = String(groupName).trim();

                if (emitUpdatedData) {
                    bus.$emit(
                        'updateGroupInfoDataBus',
                        groupData
                    );
                }

                return groupData;
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to load group information.'
                    )
                );

                return null;
            } finally {
                this.isLoading = false;
            }
        },

        async submitGroupName() {
            if (this.submitted) {
                return;
            }

            const isValid = await this.$validator.validateAll();

            if (!isValid) {
                return;
            }

            if (!this.normalizedTopic) {
                toast.error('Please enter a group name.');
                return;
            }

            if (!this.hasGroupNameChanged) {
                toast.error('Please enter a different group name.');
                return;
            }

            this.submitted = true;

            try {
                const response = await this.$store.dispatch(
                    'updateGroupChatDetails',
                    {
                        data: {
                            id: this.updateGroupNameID,
                            data: {
                                name: this.normalizedTopic
                            }
                        }
                    }
                );

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'Group name updated successfully.'
                    )
                );

                await this.fetchGroupInfo(true);

                await Promise.all([
                    this.$store.dispatch('fetchPinnedChatRooms'),
                    this.$store.dispatch('fetchNormalChatRooms')
                ]);

                this.closePopup(true);
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to update the group name.'
                    )
                );
            } finally {
                this.submitted = false;
            }
        },

        getGroupData(response) {
            if (
                response &&
                response.data &&
                response.data.data
            ) {
                return response.data.data;
            }

            return null;
        },

        getSuccessMessage(response, fallbackMessage) {
            if (
                response &&
                response.data &&
                response.data.msg
            ) {
                return response.data.msg;
            }

            return fallbackMessage;
        },

        getErrorMessage(error, fallbackMessage) {
            if (
                error &&
                error.response &&
                error.response.data
            ) {
                return (
                    error.response.data.msg ||
                    error.response.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.data) {
                return (
                    error.data.msg ||
                    error.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.message) {
                return error.message;
            }

            return fallbackMessage;
        },

        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.closePopup();
            }
        },

        closePopup(forceClose = false) {
            if (this.submitted && !forceClose) {
                return;
            }

            this.removeGlobalListeners();

            bus.$emit(
                'updateGroupNamePopupStateBus',
                false
            );
        },

        removeGlobalListeners() {
            document.body.classList.remove(BODY_SCROLL_CLASS);

            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$modal-overlay: rgba(0, 0, 0, 0.92);
$modal-background: #000000;
$text-primary: #ffffff;
$text-secondary: #cccccc;
$accent-primary: #ffd400;
$accent-secondary: #87cefa;
$danger-color: #ff3a30;
$error-color: #dc3545;
$border-color: rgba(255, 255, 255, 0.2);

.group-name-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    color: $text-primary;
    background-color: $modal-overlay;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;

    &__dialog {
        width: 100%;
        max-width: 640px;
        margin: auto;
        overflow: hidden;
        background-color: $modal-background;
        border-radius: 5px;
        box-shadow: 0 0 13px rgba(255, 213, 0, 0.6);
    }

    &__header {
        position: relative;
        display: flex;
        align-items: center;
        min-height: 60px;
        padding: 15px 55px 15px 15px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    &__title {
        margin: 0;
        color: $text-primary;
        font-size: 24px;
        font-weight: 500;
        line-height: 1.3;
    }

    &__close {
        position: absolute;
        top: 50%;
        right: 15px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        border-radius: 3px;
        color: $danger-color;
        background: transparent;
        cursor: pointer;
        transform: translateY(-50%);
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            transform 0.2s ease,
            opacity 0.2s ease;

        .fa {
            font-size: 20px;
        }

        &:hover:not(:disabled),
        &:focus-visible:not(:disabled) {
            color: $accent-secondary;
            background-color: rgba(255, 255, 255, 0.08);
            transform: translateY(-50%) scale(1.08);
        }

        &:focus-visible {
            outline: 2px solid $accent-primary;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    &__body {
        padding: 15px;
    }

    &__loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100px;
        color: $text-secondary;
        font-size: 16px;

        .fa {
            margin-right: 8px;
            color: $accent-primary;
        }
    }
}

.group-name-form {
    &__field {
        margin-bottom: 20px;
    }

    &__label {
        display: inline-block;
        margin-bottom: 5px;
        color: $text-primary;
        font-size: 16px;
        line-height: 1.3;
    }

    &__input {
        display: block;
        width: 100%;
        height: auto;
        padding: 10px 15px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 4px;
        color: $accent-primary;
        background-color: rgba(255, 255, 255, 0.08);
        font-family: inherit;
        font-size: 16px;
        line-height: 1.5;
        box-shadow: none;
        transition:
            border-color 0.2s ease,
            background-color 0.2s ease;

        &:focus {
            border-color: $accent-primary;
            outline: none;
            background-color: rgba(255, 255, 255, 0.11);
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.65;
        }

        &--invalid {
            border-color: $error-color;
        }
    }

    &__error {
        display: block;
        margin-top: 5px;
        color: $error-color;
        font-size: 14px;
        line-height: 1.3;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 105px;
        min-height: 38px;
        padding: 8px 15px;
        border: 0;
        border-radius: 3px;
        color: $text-primary;
        font-family: inherit;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        box-shadow: none;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 6px;
        }

        &:focus-visible {
            outline: 2px solid $accent-primary;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: rgba(255, 255, 255, 0.12);

            &:hover:not(:disabled),
            &:focus-visible:not(:disabled) {
                color: $text-primary;
                background-color: rgba(255, 255, 255, 0.22);
            }
        }

        &--submit {
            color: #22292f;
            background-color: $accent-secondary;

            &:hover:not(:disabled),
            &:focus-visible:not(:disabled) {
                background-color: $accent-primary;
            }
        }
    }
}

@media (max-width: 480px) {
    .group-name-modal {
        padding: 10px;

        &__header {
            padding: 12px 50px 12px 12px;
        }

        &__title {
            font-size: 20px;
        }

        &__body {
            padding: 12px;
        }
    }

    .group-name-form {
        &__actions {
            flex-direction: column-reverse;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>